import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import StepNav from '../components/StepNav';
import Step1RolesPermissions from '../components/steps/Step1RolesPermissions';
import Step2StaffAssignments from '../components/steps/Step2StaffAssignments';
import Step3Rooms from '../components/steps/Step3Rooms';
import Step4Equipment from '../components/steps/Step4Equipment';
import Step5Tasks from '../components/steps/Step5Tasks';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';

const STEP_COMPONENTS = [
  Step1RolesPermissions,
  Step2StaffAssignments,
  Step3Rooms,
  Step4Equipment,
  Step5Tasks,
];

export default function OnboardingPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeStep = Math.min(
    Math.max(parseInt(searchParams.get('step') ?? '0', 10) || 0, 0),
    STEP_COMPONENTS.length - 1
  );
  const setActiveStep = (index) => setSearchParams({ step: index }, { replace: true });
  const { steps, loading, refresh } = useOnboardingProgress();

  // Re-check progress when returning from equipment/task pages
  useEffect(() => {
    refresh();
  }, [location.key, refresh]);

  const allComplete = steps.length > 0 && steps.every(s => s.completed);
  const StepComponent = STEP_COMPONENTS[activeStep];

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        minHeight: 0,
        flex: 1,
      }}
    >
      {loading && steps.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <StepNav
            steps={steps}
            activeStep={activeStep}
            onStepClick={setActiveStep}
          />

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 4,
            }}
          >
            {allComplete && (
              <Alert
                severity="success"
                icon={<CheckCircle />}
                sx={{ mb: 3 }}
              >
                <Typography fontWeight={700}>{t('onboarding:complete.title')}</Typography>
                {t('onboarding:complete.body')}
              </Alert>
            )}

            {StepComponent && (
              <StepComponent onRefresh={refresh} />
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
